import Config from "../_config/config";
import myAxios from "./axiositeachWrapper";

const BASE = `${Config.nodeSiteUrl}v1/payment-gateway`;

export type GatewayCode = "flywire" | "dbs_qr" | "2c2p" | "razorpay";

export interface PaymentGatewayPublicConfig {
  enabledGateways: GatewayCode[];
  /** payment_gateway_detail.Id of the gateway shown first in the ilearn picker
   *  (6=razorpay, 8=flywire, 10=2c2p, 11=dbs_qr). 0 = "Auto (first available)". */
  defaultGatewayId: number;
  defaultInstrumentTypeId: number;
  excessCreditTypeId: number;
  flywire: { popupUrl: string | null; configured: boolean; amountEditable: boolean };
  // dbs_qr: merchant/PayNow config in JsonConfig.payment.dbs_qr. QR/callback
  // owned by the Java backend; the admin edits the subset below. Secrets follow
  // the 2c2p pattern — never returned raw, only a `*Configured` boolean.
  dbs_qr: {
    orgId: string | null;
    accountNo: string | null;
    proxyType: "UEN" | "MOBILE";
    proxyValue: string | null;
    merchantName: string | null;
    merchantCity: string | null;
    qrExpiryMinutes: number | null;
    amountEditable: boolean;
    callbackSecretConfigured: boolean;
    ourPrivateSignKeyConfigured: boolean;
    ourPrivateDecryptKeyConfigured: boolean;
    configured: boolean;
    /** Read-only, from customer_detail_master.iLearnAppConfig.ilearnAppEndpoint. */
    frontendReturnUrl: string | null;
  };
  "2c2p": {
    merchantId: string | null;
    apiBaseUrl: string | null;
    frontendReturnUrl: string | null;
    secretKeyConfigured: boolean;
    configured: boolean;
    /** Whether students may edit the amount on ilearn /makePayment. */
    amountEditable: boolean;
    /** "sandboxCreds" / "liveCreds" when the JsonConfig.payment.2c2p.activeCreds
     *  toggle is set. The form's merchantId/apiBaseUrl above are sourced from
     *  the matching sub-block; saves write back to it. NULL when no toggle is
     *  configured (legacy flat shape). */
    activeCreds: "sandboxCreds" | "liveCreds" | null;
  };
  razorpay: {
    /** Razorpay's PUBLIC key — safe to display; checkout.js needs it in-browser. */
    keyId: string | null;
    apiBaseUrl: string | null;
    keySecretConfigured: boolean;
    webhookSecretConfigured: boolean;
    configured: boolean;
    amountEditable: boolean;
    activeCreds: "sandboxCreds" | "liveCreds" | null;
    /** True when the live keys still come from the legacy Java row
     *  (payment_gateway_detail Id 6). Saving this tab migrates them into
     *  JsonConfig, encrypted. */
    usingLegacyCreds: boolean;
  };
}

export interface PaymentGatewayConfigInput {
  enabledGateways?: GatewayCode[];
  /** 0 (or unset) clears the override → runtime falls back to the first ready gateway. */
  defaultGatewayId?: number;
  defaultInstrumentTypeId?: number;
  excessCreditTypeId?: number;
  flywire?: { popupUrl?: string; amountEditable?: boolean };
  dbs_qr?: {
    orgId?: string;
    accountNo?: string;
    proxyType?: "UEN" | "MOBILE";
    proxyValue?: string;
    merchantName?: string;
    merchantCity?: string;
    qrExpiryMinutes?: number;
    amountEditable?: boolean;
    // Plaintext secret inputs — encrypted on save, blank = keep existing.
    callbackSecret?: string;
    ourPrivateSignKeyPem?: string;
    ourPrivateDecryptKeyPem?: string;
    // Editable — written back to iLearnAppConfig.ilearnAppEndpoint.
    frontendReturnUrl?: string;
  };
  "2c2p"?: {
    merchantId?: string;
    apiBaseUrl?: string;
    frontendReturnUrl?: string;
    secretKey?: string;
    amountEditable?: boolean;
  };
  razorpay?: {
    keyId?: string;
    apiBaseUrl?: string;
    amountEditable?: boolean;
    // Plaintext secret inputs — encrypted on save, blank = keep existing.
    keySecret?: string;
    webhookSecret?: string;
  };
}

// NOTE: myAxios is a plain-JS wrapper, so the generic on .get<T> is mis-bound
// to the request body, not the response. Convention is to type as <any>.
class PaymentGatewayService {
  getConfig() {
    return myAxios.get<any>(`${BASE}/admin/config`);
  }

  saveConfig(input: PaymentGatewayConfigInput) {
    return myAxios.put<any>(`${BASE}/admin/config`, input);
  }
}

export default new PaymentGatewayService();
